/**
 * Fantasy Console 0 - Like Service
 *
 * Handles cart like/unlike operations with Supabase.
 */

import { supabase } from '../lib/supabase'

/**
 * Like service for cart like operations
 */
export const LikeService = {
  /**
   * Like a cart
   * @param cartId The cart ID to like
   */
  async likeCart(cartId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('You must be signed in to like a cart')
    }

    const { error } = await supabase
      .from('cart_likes')
      .insert({ cart_id: cartId, user_id: user.id })

    if (error) {
      // Ignore duplicate key error (user already liked this cart)
      if (error.code === '23505') {
        return
      }
      console.error('Failed to like cart:', error)
      throw new Error(`Failed to like cart: ${error.message}`)
    }
  },

  /**
   * Unlike a cart
   * @param cartId The cart ID to unlike
   */
  async unlikeCart(cartId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('You must be signed in to unlike a cart')
    }

    const { error } = await supabase
      .from('cart_likes')
      .delete()
      .eq('cart_id', cartId)
      .eq('user_id', user.id)

    if (error) {
      console.error('Failed to unlike cart:', error)
      throw new Error(`Failed to unlike cart: ${error.message}`)
    }
  },

  /**
   * Toggle like status for a cart
   * @param cartId The cart ID to toggle
   * @param isCurrentlyLiked Whether the cart is currently liked
   */
  async toggleLike(cartId: string, isCurrentlyLiked: boolean): Promise<void> {
    if (isCurrentlyLiked) {
      await this.unlikeCart(cartId)
    } else {
      await this.likeCart(cartId)
    }
  },
}
